import { supabase } from '@/lib/supabase/client';
import { getExcludedDealers } from '@/features/recalls/services/queryBuilder';

/**
 * Debug why Georgia recalls aren't showing up in the zone map queries
 * when they exist in the database
 */

export async function debugGeorgiaQuery(userId?: string) {
  console.log('🔍 Debugging Georgia query filtering...');

  try {
    // 1. First, confirm Georgia data exists
    console.log('📊 Step 1: Confirming Georgia data exists...');

    const {
      data: gaData,
      error: gaError,
      count
    } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select('*', { count: 'exact' })
      .eq('seller_region', 'GA')
      .limit(1);

    if (gaError) {
      console.error('❌ Error querying GA data:', gaError);
      return;
    }

    console.log(`✅ Confirmed: ${count} total GA vehicles in materialized view`);
    if (gaData && gaData.length > 0) {
      console.log('📍 Sample GA record:', {
        dealer: gaData[0].service_provider_name,
        city: gaData[0].seller_city,
        lat: gaData[0].seller_latitude,
        lng: gaData[0].seller_longitude,
        zip: gaData[0].seller_postal_code
      });
    }

    // 2. Test the exact query used by spatial clustering
    console.log('\n🗺️ Step 2: Testing spatial clustering query with GA bounds...');

    const GA_BOUNDS = {
      north: 35.0,
      south: 30.3,
      east: -80.8,
      west: -85.6
    };

    // This is the exact query from spatialClustering.ts
    const query = supabase
      .from('cars_with_safety_recalls_mv')
      .select(
        `
        service_provider_name,
        seller_latitude,
        seller_longitude,
        seller_street,
        seller_city,
        seller_region,
        seller_postal_code,
        vin,
        safety_recalls
      `
      )
      .gte('seller_latitude', GA_BOUNDS.south)
      .lte('seller_latitude', GA_BOUNDS.north)
      .gte('seller_longitude', GA_BOUNDS.west)
      .lte('seller_longitude', GA_BOUNDS.east)
      .not('seller_latitude', 'is', null)
      .not('seller_longitude', 'is', null)
      .limit(100);

    const { data: spatialData, error: spatialError } = await query;

    if (spatialError) {
      console.error('❌ Error with spatial query:', spatialError);
    } else {
      console.log(`🗺️ Spatial query returned: ${spatialData?.length || 0} vehicles`);
      if (spatialData && spatialData.length > 0) {
        console.log('📍 States found in spatial query:');
        const states = [...new Set(spatialData.map(d => d.seller_region))];
        states.forEach(state => {
          const count = spatialData.filter(d => d.seller_region === state).length;
          console.log(`  ${state}: ${count} vehicles`);
        });

        console.log('📍 Sample spatial results:');
        spatialData.slice(0, 3).forEach((record, i) => {
          console.log(
            `  ${i + 1}. ${record.service_provider_name} - ${record.seller_city}, ${record.seller_region}`
          );
          console.log(`     Coords: ${record.seller_latitude}, ${record.seller_longitude}`);
        });
      }
    }

    // 3. Check if coordinates are the issue
    console.log('\n📍 Step 3: Checking coordinate filtering...');

    const { data: gaCoords, error: coordsError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select('seller_latitude, seller_longitude, seller_city, service_provider_name')
      .eq('seller_region', 'GA')
      .not('seller_latitude', 'is', null)
      .not('seller_longitude', 'is', null)
      .limit(20);

    if (coordsError) {
      console.error('❌ Error getting GA coordinates:', coordsError);
    } else if (gaCoords) {
      console.log(`📍 Found ${gaCoords.length} GA vehicles with coordinates`);

      // Check if any coordinates fall within our bounds
      const inBounds = gaCoords.filter(record => {
        const lat = record.seller_latitude;
        const lng = record.seller_longitude;
        return (
          lat >= GA_BOUNDS.south &&
          lat <= GA_BOUNDS.north &&
          lng >= GA_BOUNDS.west &&
          lng <= GA_BOUNDS.east
        );
      });

      console.log(`📍 ${inBounds.length} GA vehicles fall within bounds`);
      console.log(`📍 ${gaCoords.length - inBounds.length} GA vehicles fall OUTSIDE bounds`);

      if (gaCoords.length > inBounds.length) {
        console.log('🚨 Some GA vehicles have coordinates outside expected bounds:');
        gaCoords
          .filter(record => {
            const lat = record.seller_latitude;
            const lng = record.seller_longitude;
            return !(
              lat >= GA_BOUNDS.south &&
              lat <= GA_BOUNDS.north &&
              lng >= GA_BOUNDS.west &&
              lng <= GA_BOUNDS.east
            );
          })
          .slice(0, 5)
          .forEach((record, i) => {
            console.log(`  ${i + 1}. ${record.service_provider_name} - ${record.seller_city}`);
            console.log(
              `     Coords: ${record.seller_latitude}, ${record.seller_longitude} (outside bounds)`
            );
          });
      }

      if (inBounds.length > 0) {
        console.log('✅ GA vehicles within bounds:');
        inBounds.slice(0, 3).forEach((record, i) => {
          console.log(`  ${i + 1}. ${record.service_provider_name} - ${record.seller_city}`);
          console.log(`     Coords: ${record.seller_latitude}, ${record.seller_longitude}`);
        });
      }
    }

    // 4. Check dealer exclusions
    if (userId) {
      console.log('\n🚫 Step 4: Checking dealer exclusions...');

      const excludedDealers = await getExcludedDealers(supabase, userId);
      console.log(`🚫 Found ${excludedDealers.length} excluded dealers for user`);

      if (excludedDealers.length > 0) {
        console.log('🚫 Excluded dealers:', excludedDealers.slice(0, 5));

        // Check if any GA dealers are excluded
        const { data: gaDealers, error: dealersError } = await supabase
          .from('cars_with_safety_recalls_mv')
          .select('service_provider_name')
          .eq('seller_region', 'GA')
          .in('service_provider_name', excludedDealers);

        if (!dealersError && gaDealers) {
          console.log(`🚫 ${gaDealers.length} GA dealers are in exclusion list`);
        }
      }
    }

    // 5. Test query with exclusions applied
    console.log('\n🔍 Step 5: Testing full query with all filters...');

    let fullQuery = supabase
      .from('cars_with_safety_recalls_mv')
      .select(
        'service_provider_name, seller_city, seller_region, seller_latitude, seller_longitude'
      )
      .gte('seller_latitude', GA_BOUNDS.south)
      .lte('seller_latitude', GA_BOUNDS.north)
      .gte('seller_longitude', GA_BOUNDS.west)
      .lte('seller_longitude', GA_BOUNDS.east)
      .not('seller_latitude', 'is', null)
      .not('seller_longitude', 'is', null)
      .limit(100);

    // Apply dealer exclusions if provided
    if (userId) {
      const excludedDealers = await getExcludedDealers(supabase, userId);
      if (excludedDealers.length > 0) {
        excludedDealers.forEach(dealerName => {
          fullQuery = fullQuery.not('service_provider_name', 'eq', dealerName);
        });
      }
    }

    const { data: fullData, error: fullError } = await fullQuery;

    if (fullError) {
      console.error('❌ Error with full query:', fullError);
    } else {
      console.log(`🔍 Full filtered query returned: ${fullData?.length || 0} vehicles`);
      if (fullData && fullData.length > 0) {
        const gaInFull = fullData.filter(d => d.seller_region === 'GA');
        console.log(`🍑 ${gaInFull.length} GA vehicles in final results`);
      }
    }

    return {
      gaRecordsTotal: count || 0,
      spatialQueryResults: spatialData?.length || 0,
      coordinatesInBounds: inBounds?.length || 0,
      finalQueryResults: fullData?.length || 0
    };
  } catch (error) {
    console.error('❌ Debug failed:', error);
    return null;
  }
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugGeorgiaQuery = debugGeorgiaQuery;
}

export default debugGeorgiaQuery;
